import { Injectable } from '@angular/core';
import {
  ErreurInput,
  ErreursInputFormAdresse,
  ErreursInputFormCourriel,
  ErreursInputFormTelephone,
} from '../../models/erreurs/erreur-input';
import { FormGroup } from '@angular/forms';
import { FormulaireAdresseCanada } from '../../models/formulaire/formulaire-coordonnees.model';
import { ErreurSummary } from '../../models/erreurs/erreur-summary';
import { ValidationError } from '../../models/erreurs/validation-error';
import { TranslateService } from '@ngx-translate/core';
import { FormulaireCourriel } from '../../models/formulaire/formulaire-courriel.model';
import { FormulaireBuilder } from '../../models/formulaire/formulaire-builder.model';
import { FormulaireTelephone } from '../../models/formulaire/formulaire-telephones.model';
import { FormulaireCoordonneesBancaires } from 'src/app/models/formulaire/formulaire-coordonnees-bancaires.model';

@Injectable({
  providedIn: 'root',
})
export class ErreurMappingService {
  initial = true;
  formGroupAdresseCanada: FormGroup<FormulaireAdresseCanada>;
  formGroupTelephone: FormGroup<FormulaireTelephone>;
  formGroupCourriel: FormGroup<FormulaireCourriel>;
  formGroupBancaire: FormGroup<FormulaireCoordonneesBancaires>;

  constructor(private readonly translateService: TranslateService) {}

  resetFormsError() {
    this.getFormErrorAdresseCanada(FormulaireBuilder.canada(), true);
    this.getFormErrorCourriel(FormulaireBuilder.courriel(), true);
    this.getFormErrorTelephone(FormulaireBuilder.telephone(), true);
  }

  getFormErrorTelephone(
    formGroupControls: FormGroup<FormulaireTelephone>,
    initial: boolean = false
  ): ErreursInputFormTelephone {
    this.formGroupTelephone = formGroupControls;

    const erreursForm = {
      principal: this.getErreurInputTelephonePrincipal(),
      secondaire: this.getErreurInputTelephoneSecondaire(),
      employeur: this.getErreurInputTelephoneEmployeur(),
      numeroPoste: this.getErreurInputPosteEmployeur(),
    };

    this.initial = initial;

    return erreursForm;
  }

  getFormErrorAdresseCanada(
    formGroupControls: FormGroup<FormulaireAdresseCanada>,
    initial: boolean = false
  ): ErreursInputFormAdresse {
    this.formGroupAdresseCanada = formGroupControls;

    const erreursForm = {
      rue: this.getErreurInputRue(),
      appartement: this.getErreurInputAppartement(),
      codePostal: this.getErreurInputCodePostal(),
      province: this.getErreurInputProvince(),
      ville: this.getErreurInputVille(),
    };

    this.initial = initial;

    return erreursForm;
  }

  getFormErrorCourriel(
    formGroupControls: FormGroup<FormulaireCourriel>,
    initial: boolean = false
  ): ErreursInputFormCourriel {
    this.formGroupCourriel = formGroupControls;
    const erreursForm = {
      courriel: this.getErreurInputCourriel(),
    };
    this.initial = initial;

    return erreursForm;
  }

  getErrorAdresseFromErrorForm(erreursInput: ErreursInputFormAdresse): ErreurSummary[] {
    let erreursSummary: ErreurSummary[] = [];

    const rueErreurSummary = this.getErrorSummaryForField('rueCanada', erreursInput.rue);
    const appartementErreurSummary = this.getErrorSummaryForField('appartementCanada', erreursInput.appartement);
    const codePostalErreurSummary = this.getErrorSummaryForField('codePostalCanada', erreursInput.codePostal);
    const provinceErreurSummary = this.getErrorSummaryForField('provinceCanada', erreursInput.province);
    const villeErreurSummary = this.getErrorSummaryForField('villeCanada', erreursInput.ville);

    erreursSummary.push(
      rueErreurSummary,
      appartementErreurSummary,
      codePostalErreurSummary,
      provinceErreurSummary,
      villeErreurSummary
    );
    erreursSummary = erreursSummary.filter((x) => x !== null);

    return erreursSummary;
  }

  getErrorCourrielFromErrorForm(erreursInput: ErreursInputFormCourriel): ErreurSummary[] {
    let erreursSummary: ErreurSummary[] = [];
    const champErreurSummary = this.getErrorSummaryForField('courriel', erreursInput.courriel);

    erreursSummary.push(champErreurSummary);
    erreursSummary = erreursSummary.filter((x) => x !== null);

    return erreursSummary;
  }

  getErrorTelephoneFromErrorForm(erreursInput: ErreursInputFormTelephone): ErreurSummary[] {
    let erreursSummary: ErreurSummary[] = [];
    const TELEPHONE_PRINCIPAL_ERREUR_SUMMARY = this.getErrorSummaryForField('principal', erreursInput.principal);
    const TELEPHONE_SECONDAIRE_ERREUR_SUMMARY = this.getErrorSummaryForField('secondaire', erreursInput.secondaire);
    const TELEPHONE_EMPLOYEUR_ERREUR_SUMMARY = this.getErrorSummaryForField('employeur', erreursInput.employeur);
    const POSTE_EMPLOYEUR_ERREUR_SUMMARY = this.getErrorSummaryForField('numeroPoste', erreursInput.numeroPoste);

    erreursSummary.push(
      TELEPHONE_PRINCIPAL_ERREUR_SUMMARY,
      TELEPHONE_SECONDAIRE_ERREUR_SUMMARY,
      TELEPHONE_EMPLOYEUR_ERREUR_SUMMARY,
      POSTE_EMPLOYEUR_ERREUR_SUMMARY
    );
    erreursSummary = erreursSummary.filter((x) => x !== null);
    erreursSummary = erreursSummary.filter((x) => x !== null);

    return erreursSummary;
  }

  getErrorTextFromMappingErreurInput(erreurInput: ErreurInput): string {
    if (erreurInput.currentError) {
      let keys: string[] = [];

      Object.entries(erreurInput.currentError).forEach(([key]) => {
        keys.push(key);
      });

      if (keys.length > 1 && keys.includes(ValidationError.PATTERN)) {
        keys = keys.filter((x) => x !== ValidationError.PATTERN);
      }

      return erreurInput.errorsMapping[keys[0]];
    }
    return '';
  }

  private getErrorSummaryForField(nom: string, erreurInput: ErreurInput): ErreurSummary | null {
    if (erreurInput.hasError && erreurInput.currentError) {
      return { id: nom, text: this.translateService.instant(erreurInput.nameInput) };
    }
    return null;
  }

  private getErreurInputCourriel(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.courriel.titre',
      currentError: this.formGroupCourriel.controls.courriel.errors,
      hasError: this.initial ? false : this.formGroupCourriel.controls.courriel.errors !== null,
      errorsMapping: {
        required: 'formulairePosteCanada.courriel.erreur.required',
        textevide: 'formulairePosteCanada.courriel.erreur.textevide',
        maxlength: 'formulairePosteCanada.courriel.erreur.maxLength',
        courriel: 'formulairePosteCanada.courriel.erreur.courriel',
      },
    };
  }

  private getErreurInputRue(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.coordonnees.rue.titre',
      currentError: this.formGroupAdresseCanada.controls.rueCanada.errors,
      hasError: this.initial ? false : this.formGroupAdresseCanada.controls.rueCanada.errors !== null,
      errorsMapping: {
        required: 'formulairePosteCanada.coordonnees.rue.erreur.required',
        textevide: 'formulairePosteCanada.coordonnees.rue.erreur.textevide',
        separation: 'formulairePosteCanada.coordonnees.rue.erreur.separation',
        maxlength: 'formulairePosteCanada.coordonnees.rue.erreur.maxLength',
        authorizedCharacters: 'formulairePosteCanada.coordonnees.rue.erreur.authorizedCharacters',
      },
    };
  }

  private getErreurInputAppartement(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.coordonnees.appartement.titre',
      currentError: this.formGroupAdresseCanada.controls.appartementCanada.errors,
      hasError: this.initial ? false : this.formGroupAdresseCanada.controls.appartementCanada.errors !== null,
      errorsMapping: {
        maxlength: 'formulairePosteCanada.coordonnees.appartement.erreur.maxLength',
        authorizedCharacters: 'formulairePosteCanada.coordonnees.appartement.erreur.authorizedCharacters',
      },
    };
  }

  private getErreurInputVille(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.coordonnees.ville.titre',
      currentError: this.formGroupAdresseCanada.controls.villeCanada.errors,
      hasError: this.initial ? false : this.formGroupAdresseCanada.controls.villeCanada.errors !== null,
      errorsMapping: {
        required: 'formulairePosteCanada.coordonnees.ville.erreur.required',
        maxlength: 'formulairePosteCanada.coordonnees.ville.erreur.maxLength',
        ville: 'formulairePosteCanada.coordonnees.ville.erreur.ville',
      },
    };
  }

  private getErreurInputProvince(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.coordonnees.province.titre',
      currentError: this.formGroupAdresseCanada.controls.provinceCanada.errors,
      hasError: this.initial ? false : this.formGroupAdresseCanada.controls.provinceCanada.errors !== null,
      errorsMapping: {
        required: 'formulairePosteCanada.coordonnees.province.erreur',
      },
    };
  }

  private getErreurInputCodePostal(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.coordonnees.codePostal.titre',
      currentError: this.formGroupAdresseCanada.controls.codePostalCanada.errors,
      hasError: this.initial ? false : this.formGroupAdresseCanada.controls.codePostalCanada.errors !== null,
      errorsMapping: {
        required: 'formulairePosteCanada.coordonnees.codePostal.erreur.required',
        codepostal: 'formulairePosteCanada.coordonnees.codePostal.erreur.codepostal',
        authorizedCharacters: 'formulairePosteCanada.coordonnees.codePostal.erreur.authorizedCharacters',
      },
    };
  }

  private getErreurInputTelephonePrincipal(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.telephone.telephonePrincipal.titre',
      currentError: this.formGroupTelephone.controls.principal.errors,
      hasError: this.initial ? false : this.formGroupTelephone.controls.principal.errors !== null,
      errorsMapping: {
        required: 'formulairePosteCanada.telephone.erreur.required',
        numeroTelephone: 'formulairePosteCanada.telephone.erreur.numeroTelephone',
        maxlength: 'formulairePosteCanada.telephone.erreur.maxlength',
        authorizedCharacters: 'formulairePosteCanada.telephone.erreur.authorizedCharacters',
      },
    };
  }

  private getErreurInputTelephoneSecondaire(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.telephone.telephoneSecondaire.titre',
      currentError: this.formGroupTelephone.controls.secondaire.errors,
      hasError: this.initial ? false : this.formGroupTelephone.controls.secondaire.errors !== null,
      errorsMapping: {
        required: 'formulairePosteCanada.telephone.erreur.required',
        numeroTelephone: 'formulairePosteCanada.telephone.erreur.numeroTelephone',
        maxlength: 'formulairePosteCanada.telephone.erreur.maxlength',
        authorizedCharacters: 'formulairePosteCanada.telephone.erreur.authorizedCharacters',
      },
    };
  }

  private getErreurInputTelephoneEmployeur(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.telephone.telephoneEmployeur.titre',
      currentError: this.formGroupTelephone.controls.employeur.errors,
      hasError: this.initial ? false : this.formGroupTelephone.controls.employeur.errors !== null,
      errorsMapping: {
        required: 'formulairePosteCanada.telephone.erreur.required',
        numeroTelephone: 'formulairePosteCanada.telephone.erreur.numeroTelephone',
        maxlength: 'formulairePosteCanada.telephone.erreur.maxlength',
        authorizedCharacters: 'formulairePosteCanada.telephone.erreur.authorizedCharacters',
      },
    };
  }

  private getErreurInputPosteEmployeur(): ErreurInput {
    return {
      nameInput: 'formulairePosteCanada.telephone.numeroPoste.titre',
      currentError: this.formGroupTelephone.controls.numeroPoste.errors,
      hasError: this.initial ? false : this.formGroupTelephone.controls.numeroPoste.errors !== null,
      errorsMapping: {
        numeroPoste: 'formulairePosteCanada.telephone.numeroPoste.erreur.numeroPoste',
        maxlength: 'formulairePosteCanada.telephone.numeroPoste.erreur.maxlength',
        authorizedCharacters: 'formulairePosteCanada.telephone.numeroPoste.erreur.authorizedCharacters',
      },
    };
  }
}
